import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@xiltrion/ui/button';
import { Icono } from '@xiltrion/ui/icono';
import { Input } from '@xiltrion/ui/input';
import { Spinner } from '@xiltrion/ui/spinner';
import { NIT_MAX_DIGITS, PLACEHOLDER_ENTITY_NAME, nitCheckDigit, realEntityName } from '@pqrs/contracts';
import { apiPatch } from '@/api/client';
import { Card, ErrorState, LoadingState, PageHeader } from '@/components/ui';
import { useEntity, qk } from './api';
import { Field, notify, notifyError } from './shared';
import { BrandingCards } from './BrandingCards';
import { SuiteManagedAlert, useSuiteManagesEntity } from './suite-managed';

/**
 * Entidad: its name and NIT (they sign the emails to the citizen and the electronic index), its website and phone (they
 * go in the signed responses), and its brand: logo,
 * letterhead of the PDFs it issues and the colour of its screens (citizen portal and backoffice).
 */
export function EntityPage() {
  return (
    <div className="space-y-6">
      <PageHeader title="Entidad" subtitle="Nombre, NIT, contacto y marca de la entidad: el logo, el membrete de sus documentos y el color del portal ciudadano y de esta aplicación." />
      <EntityCard />
      <BrandingCards />
    </div>
  );
}

/** Digits of the NIT before its check digit: what is typed here and what the API takes («900.123.456-7» → 900123456). */
const nitDigits = (v: string) => v.split('-')[0]!.replace(/\D/g, '').slice(0, NIT_MAX_DIGITS);

function EntityCard() {
  const qc = useQueryClient();
  const entity = useEntity();
  const suite = useSuiteManagesEntity();
  const [f, setF] = useState({ name: '', nit: '', website: '', phone: '' });
  useEffect(() => { if (entity.data) setF({ name: realEntityName(entity.data.name) ?? '', nit: nitDigits(entity.data.nit ?? ''), website: entity.data.website ?? '', phone: entity.data.phone ?? '' }); }, [entity.data]);
  const save = useMutation({ mutationFn: () => apiPatch('/organizations/entity', { name: f.name.trim(), nit: f.nit || null, website: f.website.trim() || null, phone: f.phone.trim() || null }), onSuccess: () => { void qc.invalidateQueries({ queryKey: qk.entity }); void qc.invalidateQueries({ queryKey: ['me'] }); notify('success', 'Entidad guardada'); }, onError: notifyError });
  const canSave = !!entity.data && !save.isPending && !!f.name.trim();
  return (
    <Card title={<span className="flex items-center gap-2"><Icono nombre="mdi:domain" aria-hidden /> Nombre, NIT y contacto</span>}
      actions={entity.data && !suite && <Button size="sm" disabled={!canSave} onClick={() => save.mutate()}>{save.isPending ? <Spinner /> : <Icono nombre="mdi:check" />} Guardar</Button>}>
      {entity.isLoading ? <LoadingState rows={2} /> : entity.error ? <ErrorState error={entity.error} onRetry={() => void entity.refetch()} /> : (
        <form className="flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); if (canSave && !suite) save.mutate(); }}>
          {suite && <SuiteManagedAlert url={suite.url}>El nombre, el NIT, el sitio web y el teléfono de la entidad los cambia su administrador en la suite Datalogic (Perfil y marca) y valen para todas sus apps.</SuiteManagedAlert>}
          <div className="grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-3">
            <Field label="Nombre" htmlFor="e-name" required><Input id="e-name" readOnly={!!suite} value={f.name} maxLength={200} placeholder={PLACEHOLDER_ENTITY_NAME} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
            <div className="grid grid-cols-[minmax(0,1fr)_4rem] gap-3">
              <Field label="NIT" htmlFor="e-nit" hint={suite ? undefined : 'Sin el dígito de verificación.'}><Input id="e-nit" className="tnum" readOnly={!!suite} value={f.nit} inputMode="numeric" placeholder="900123456" onChange={(e) => setF({ ...f, nit: nitDigits(e.target.value) })} /></Field>
              <Field label="DV" htmlFor="e-dv"><Input id="e-dv" className="tnum text-center bg-muted" value={f.nit ? String(nitCheckDigit(f.nit)) : ''} readOnly tabIndex={-1} aria-label="Dígito de verificación" /></Field>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Sitio web" htmlFor="e-web" hint={suite ? undefined : 'Sale en las respuestas firmadas, en la franja lateral y bajo la firma.'}><Input id="e-web" readOnly={!!suite} value={f.website} maxLength={200} placeholder="www.entidad.gov.co" onChange={(e) => setF({ ...f, website: e.target.value })} /></Field>
            <Field label="Línea de atención" htmlFor="e-phone" hint={suite ? undefined : 'Sale bajo la firma de las respuestas, tal como se escribe.'}><Input id="e-phone" readOnly={!!suite} value={f.phone} maxLength={120} placeholder="Call Center Virtual 3206564561" onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
          </div>
        </form>
      )}
    </Card>
  );
}
